import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';
const SUCCESS = '#36B37E';
const DANGER = '#DE350B';
const WARN = '#FF8B00';

export default function ZonesPage() {
  const [activeTab, setActiveTab] = useState('zones'); // 'zones' | 'vlans'
  const [zones, setZones] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [olts, setOlts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [form, setForm] = useState({ name: '', township: '', region: '', description: '', pppoe_prefix: '', next_counter: 1, is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncingOlt, setSyncingOlt] = useState(null);
  const [selectedOlt, setSelectedOlt] = useState('');

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      post: (url, body) => fetch(url, { method: 'POST', headers, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
      del: (url) => fetch(url, { method: 'DELETE', headers }).then(r => r.json()),
    };
  }, []);

  const loadZones = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await api.get(`/api/noc/zones${params}`);
      setZones(data.items || []);
    } catch (e) { setError('Failed to load zones'); }
    finally { setLoading(false); }
  }, [api, search]);

  const loadVlans = useCallback(async (oltId) => {
    setLoading(true);
    try {
      const params = oltId ? `?olt_id=${oltId}` : '';
      const data = await api.get(`/api/noc/vlans${params}`);
      setVlans(data.vlans || []);
    } catch (e) { setError('Failed to load VLANs'); }
    finally { setLoading(false); }
  }, [api]);

  const loadOlts = useCallback(async () => {
    try {
      const data = await api.get('/api/noc/olts');
      setOlts(data.items || data || []);
      if ((data.items || data || []).length > 0) {
        const firstId = (data.items || data)[0].id;
        setSelectedOlt(String(firstId));
        loadVlans(firstId);
      }
    } catch (e) {}
  }, [api, loadVlans]);

  useEffect(() => { loadZones(); }, [loadZones]);
  useEffect(() => { if (activeTab === 'vlans') loadOlts(); }, [activeTab, loadOlts]);

  const openAdd = () => {
    setEditZone(null);
    setForm({ name: '', township: '', region: '', description: '', pppoe_prefix: '', next_counter: 1, is_active: true });
    setShowForm(true);
    setError('');
  };
  const openEdit = (z) => {
    setEditZone(z);
    setForm({
      name: z.name || '',
      township: z.township || '',
      region: z.region || '',
      description: z.description || '',
      pppoe_prefix: z.pppoe_prefix || '',
      next_counter: z.pppoe_counter || 1,
      is_active: z.is_active !== 0,
    });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Zone name is required'); return; }
    if (!form.pppoe_prefix.trim()) { setError('PPPoE prefix is required'); return; }
    if (!/^[a-z]{2,10}$/.test(form.pppoe_prefix.trim())) { setError('PPPoE prefix: 2-10 lowercase letters only'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, pppoe_prefix: form.pppoe_prefix.trim().toLowerCase(), next_counter: parseInt(form.next_counter) || 1 };
      let res;
      if (editZone) { res = await api.put(`/api/noc/zones/${editZone.id}`, payload); }
      else { res = await api.post('/api/noc/zones', payload); }
      if (res.error || res.success === false) { setError(res.error || 'Save failed'); return; }
      setShowForm(false);
      setSuccess(editZone ? 'Zone updated!' : 'Zone created!');
      setTimeout(() => setSuccess(''), 3000);
      loadZones();
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"?`)) return;
    try {
      await api.del(`/api/noc/zones/${z.id}`);
      loadZones();
    } catch (e) { setError('Delete failed'); }
  };

  const handleSyncVlans = async () => {
    if (!selectedOlt) return;
    setSyncingOlt(selectedOlt);
    try {
      const res = await api.post(`/api/noc/olts/${selectedOlt}/sync-vlans`, {});
      if (res.success) {
        setSuccess(`Synced ${res.vlans_found} VLANs from OLT`);
        setTimeout(() => setSuccess(''), 4000);
        loadVlans(selectedOlt);
      } else {
        setError(res.error || 'Sync failed');
      }
    } catch (e) { setError('Sync failed'); }
    finally { setSyncingOlt(null); }
  };

  const handleToggleVlan = async (vlan) => {
    try {
      await api.put(`/api/noc/vlans/${vlan.id}/toggle-allowed`, { is_allowed: !vlan.is_allowed, name: vlan.name || '' });
      loadVlans(selectedOlt || undefined);
    } catch (e) { setError('Update failed'); }
  };

  const handleAddVlanManual = async () => {
    const vlanId = prompt('Enter VLAN ID (1-4094):');
    if (!vlanId) return;
    const v = parseInt(vlanId);
    if (!v || v < 1 || v > 4094) { alert('Invalid VLAN ID'); return; }
    if (!selectedOlt) { alert('Select an OLT first'); return; }
    try {
      const res = await api.post('/api/noc/admin/run-migration', {
        sqls: [`INSERT IGNORE INTO noc_vlans (olt_id, vlan_id, is_allowed, created_at, updated_at) VALUES (${selectedOlt}, ${v}, 1, NOW(), NOW())`]
      });
      loadVlans(selectedOlt);
    } catch (e) { setError('Add VLAN failed'); }
  };

  const styles = {
    container: { padding: '24px', fontFamily: 'Inter, sans-serif', maxWidth: 1100, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 700, color: '#172B4D', margin: 0 },
    tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #DFE1E6' },
    tab: (active) => ({ padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, border: 'none', background: 'none', color: active ? PRIMARY : '#6B778C', borderBottom: active ? `3px solid ${PRIMARY}` : '3px solid transparent', marginBottom: -2 }),
    btn: (color) => ({ padding: '8px 16px', background: color || PRIMARY, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }),
    btnOutline: { padding: '7px 14px', background: '#fff', color: PRIMARY, border: `1.5px solid ${PRIMARY}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { background: '#F4F5F7', padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5E6C84', borderBottom: '2px solid #DFE1E6' },
    td: { padding: '10px 12px', borderBottom: '1px solid #F4F5F7', color: '#172B4D', verticalAlign: 'middle' },
    badge: (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: color === 'green' ? '#E3FCEF' : color === 'red' ? '#FFEBE6' : '#EAE6FF', color: color === 'green' ? '#006644' : color === 'red' ? '#BF2600' : '#403294' }),
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalBox: { background: '#fff', borderRadius: 10, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
    label: { display: 'block', fontWeight: 600, fontSize: 12, color: '#5E6C84', marginBottom: 4, marginTop: 14 },
    input: { width: '100%', padding: '8px 10px', border: '1.5px solid #DFE1E6', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    alert: (type) => ({ padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13, background: type === 'error' ? '#FFEBE6' : '#E3FCEF', color: type === 'error' ? '#BF2600' : '#006644' }),
    searchRow: { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' },
    vlanRow: (allowed) => ({ background: allowed ? '#E3FCEF' : '#fff', transition: 'background 0.2s' }),
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Zones & VLANs</h1>
        {activeTab === 'zones' && (
          <button style={styles.btn()} onClick={openAdd}>+ Add Zone</button>
        )}
        {activeTab === 'vlans' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={styles.btnOutline} onClick={handleAddVlanManual}>+ Add VLAN</button>
            <button style={styles.btn()} onClick={handleSyncVlans} disabled={!!syncingOlt}>
              {syncingOlt ? 'Syncing...' : '🔄 Sync from OLT'}
            </button>
          </div>
        )}
      </div>

      {error && <div style={styles.alert('error')}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>✕</button></div>}
      {success && <div style={styles.alert('success')}>{success}</div>}

      <div style={styles.tabs}>
        <button style={styles.tab(activeTab === 'zones')} onClick={() => setActiveTab('zones')}>📍 Zones ({zones.length})</button>
        <button style={styles.tab(activeTab === 'vlans')} onClick={() => setActiveTab('vlans')}>🌐 Allowed VLANs ({vlans.filter(v => v.is_allowed).length}/{vlans.length})</button>
      </div>

      {activeTab === 'zones' && (
        <>
          <div style={styles.searchRow}>
            <input style={{ ...styles.input, maxWidth: 300 }} placeholder="Search zones..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {loading ? <p style={{ color: '#6B778C' }}>Loading...</p> : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Zone Name</th>
                  <th style={styles.th}>Township / Region</th>
                  <th style={styles.th}>PPPoE Prefix</th>
                  <th style={styles.th}>Next Counter</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {zones.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#6B778C', padding: 30 }}>No zones found. Add one to get started.</td></tr>
                ) : zones.map(z => (
                  <tr key={z.id}>
                    <td style={styles.td}><strong>{z.name}</strong>{z.description ? <div style={{ fontSize: 11, color: '#6B778C' }}>{z.description}</div> : null}</td>
                    <td style={styles.td}>{[z.township, z.region].filter(Boolean).join(', ') || '—'}</td>
                    <td style={styles.td}><code style={{ background: '#F4F5F7', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{z.pppoe_prefix || '—'}</code></td>
                    <td style={styles.td}>{z.pppoe_counter || 1}</td>
                    <td style={styles.td}><span style={styles.badge(z.is_active ? 'green' : 'red')}>{z.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={styles.td}>
                      <button style={{ ...styles.btnOutline, marginRight: 6, padding: '4px 10px' }} onClick={() => openEdit(z)}>Edit</button>
                      <button style={{ ...styles.btn(DANGER), padding: '4px 10px' }} onClick={() => handleDelete(z)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeTab === 'vlans' && (
        <>
          <div style={styles.searchRow}>
            <select style={{ ...styles.input, maxWidth: 250 }} value={selectedOlt} onChange={e => { setSelectedOlt(e.target.value); if (e.target.value) loadVlans(e.target.value); }}>
              <option value="">Select OLT...</option>
              {olts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <span style={{ fontSize: 12, color: '#6B778C' }}>Toggle ✅ to allow VLAN in ONU authorization</span>
          </div>
          {loading ? <p style={{ color: '#6B778C' }}>Loading...</p> : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>VLAN ID</th>
                  <th style={styles.th}>Name / Description</th>
                  <th style={styles.th}>OLT</th>
                  <th style={styles.th}>Synced At</th>
                  <th style={styles.th}>Allowed</th>
                </tr>
              </thead>
              <tbody>
                {vlans.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#6B778C', padding: 30 }}>
                    No VLANs. Click "Sync from OLT" or "+ Add VLAN" to add.
                  </td></tr>
                ) : vlans.map(v => (
                  <tr key={v.id} style={styles.vlanRow(v.is_allowed)}>
                    <td style={styles.td}><strong>VLAN {v.vlan_id}</strong></td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, maxWidth: 200, padding: '4px 8px' }}
                        placeholder="Name..."
                        defaultValue={v.name || ''}
                        onBlur={async (e) => {
                          if (e.target.value !== (v.name || '')) {
                            await api.put(`/api/noc/vlans/${v.id}/toggle-allowed`, { is_allowed: v.is_allowed, name: e.target.value });
                          }
                        }}
                      />
                    </td>
                    <td style={styles.td}>{v.olt_name || `OLT ${v.olt_id}`}</td>
                    <td style={styles.td}>{v.synced_at ? new Date(v.synced_at).toLocaleDateString() : '—'}</td>
                    <td style={styles.td}>
                      <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={!!v.is_allowed} onChange={() => handleToggleVlan(v)} style={{ width: 16, height: 16 }} />
                        <span style={styles.badge(v.is_allowed ? 'green' : 'red')}>{v.is_allowed ? 'Allowed' : 'Blocked'}</span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {showForm && (
        <div style={styles.modal} onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={styles.modalBox}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>{editZone ? 'Edit Zone' : 'Add Zone'}</h2>
            {error && <div style={styles.alert('error')}>{error}</div>}
            <label style={styles.label}>Zone Name *</label>
            <input style={styles.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Yangon" />
            <label style={styles.label}>PPPoE Prefix * <span style={{ fontWeight: 400, color: '#6B778C' }}>(2-10 lowercase letters)</span></label>
            <input style={styles.input} value={form.pppoe_prefix} onChange={e => setForm(f => ({ ...f, pppoe_prefix: e.target.value.toLowerCase() }))} placeholder="e.g. ygn" maxLength={10} />
            <label style={styles.label}>Next Counter</label>
            <input style={styles.input} type="number" min={1} value={form.next_counter} onChange={e => setForm(f => ({ ...f, next_counter: parseInt(e.target.value) || 1 }))} />
            <label style={styles.label}>Township</label>
            <input style={styles.input} value={form.township} onChange={e => setForm(f => ({ ...f, township: e.target.value }))} placeholder="e.g. Hlaing" />
            <label style={styles.label}>Region</label>
            <input style={styles.input} value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} placeholder="e.g. Yangon Region" />
            <label style={styles.label}>Description</label>
            <input style={styles.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button style={styles.btnOutline} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.btn()} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
